/**
 * Pause controls (plan §12, §18 "Pause controls"): hierarchical policies with
 * blocking reasons, per-account progress (requested → device applied →
 * cancellations pending → no resting orders confirmed → positions held /
 * unknown offline exposure). Resume removes only the selected policy.
 */
import { useState } from "react";
import { Alert, Badge, Button, Group, Paper, Select, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { upgradeApi, type PauseScopeKind } from "../../api/fleet";

const KINDS: PauseScopeKind[] = ["fleet", "venue", "category", "product_family", "competition", "subscription", "event", "instrument", "bot", "assignment"];
const STATE_LABEL: Record<string, { label: string; color: string }> = {
  pause_requested: { label: "Pause requested", color: "yellow" },
  device_block_applied: { label: "Device block applied", color: "green" },
  cancellation_pending: { label: "Cancellations pending", color: "orange" },
  no_resting_orders_confirmed: { label: "No resting orders (confirmed)", color: "green" },
  positions_held: { label: "Positions still held", color: "blue" },
  unknown_offline_exposure: { label: "Unknown / offline exposure", color: "red" },
};

export function ControlsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const policies = useQuery({ queryKey: ["controls", "policies"], queryFn: () => upgradeApi.controls.policies(), refetchInterval: 10_000 });
  const tree = useQuery({ queryKey: ["catalog", "tree"], queryFn: () => upgradeApi.catalog.tree() });
  const [kind, setKind] = useState<PauseScopeKind>("product_family");
  const [scopeId, setScopeId] = useState("");
  const [reason, setReason] = useState("");
  const [inspect, setInspect] = useState<{ kind: PauseScopeKind; id: string } | null>(null);
  const status = useQuery({
    queryKey: ["controls", "status", inspect?.kind, inspect?.id],
    queryFn: () => upgradeApi.controls.status(inspect!.kind, inspect!.id),
    enabled: !!inspect, refetchInterval: 5_000,
  });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["controls"] }); void qc.invalidateQueries({ queryKey: ["fleet"] }); };
  const pause = useMutation({
    mutationFn: () => upgradeApi.controls.pause({ scope_kind: kind, scope_id: kind === "fleet" ? "fleet" : scopeId, reason: reason || undefined }),
    onSuccess: (r) => { notifications.show({ color: "yellow", title: "Pause requested", message: `${(r.affected_accounts as string[]).length} account(s) affected; watch the status panel for device application and cancellations.` }); setInspect({ kind, id: kind === "fleet" ? "fleet" : scopeId }); refresh(); },
    onError: (e) => notifications.show({ color: "red", title: "Pause failed", message: (e as Error).message }),
  });
  const resume = useMutation({
    mutationFn: (p: { scope_kind: PauseScopeKind; scope_id: string }) => upgradeApi.controls.resume(p),
    onSuccess: () => { notifications.show({ color: "green", title: "Resume applied to this scope only", message: "Other blocking policies (parents) still apply." }); refresh(); },
    onError: (e) => notifications.show({ color: "red", title: "Resume failed", message: (e as Error).message }),
  });

  const scopeOptions = (() => {
    const t = tree.data;
    if (!t) return [];
    if (kind === "category") return t.categories.map((c) => ({ value: c.id, label: `${c.label} (${c.kind})` }));
    if (kind === "product_family") return t.product_families.map((p) => ({ value: p.id, label: `${p.label} [${p.legacy_market_id}]` }));
    if (kind === "competition") return t.competitions.map((c) => ({ value: c.id, label: `${c.label} (${c.sport})` }));
    if (kind === "subscription") return (t.subscriptions ?? []).map((s) => ({ value: s.id, label: s.label }));
    return [];
  })();

  return (
    <>
      {!embedded && <Title order={2} mb="lg">Pause Controls</Title>}
      <Alert color="yellow" mb="md" title="What a pause does">
        A pause blocks buys, sells and manual orders in its scope and requests cancellation of resting orders. Collection, reporting,
        accounting and resolution tracking continue. An order already submitted — or inside the venue's non-cancelable delay — may still fill.
      </Alert>
      <Paper withBorder p="md" mb="lg">
        <Group align="end">
          <Select label="Scope kind" data={KINDS} value={kind} onChange={(v) => { setKind((v as PauseScopeKind) ?? "fleet"); setScopeId(""); }} w={180} />
          {kind !== "fleet" && (scopeOptions.length
            ? <Select label="Scope" data={scopeOptions} value={scopeId} onChange={(v) => setScopeId(v ?? "")} searchable w={{ base: "100%", sm: 360 }} />
            : <TextInput label="Scope id" value={scopeId} onChange={(e) => setScopeId(e.currentTarget.value)} w={{ base: "100%", sm: 360 }} />)}
          <TextInput label="Reason (audited)" value={reason} onChange={(e) => setReason(e.currentTarget.value)} w={{ base: "100%", sm: 260 }} />
          <Button color="red" onClick={() => pause.mutate()} loading={pause.isPending} disabled={kind !== "fleet" && !scopeId}>Pause scope</Button>
          <Button variant="light" onClick={() => setInspect({ kind, id: kind === "fleet" ? "fleet" : scopeId })} disabled={kind !== "fleet" && !scopeId}>Inspect status</Button>
        </Group>
      </Paper>

      <Title order={4} mb="xs">Active policies</Title>
      <Paper withBorder p="xs" mb="lg">
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Scope</Table.Th><Table.Th>State</Table.Th><Table.Th>Reason</Table.Th><Table.Th>Actor</Table.Th><Table.Th>Revision</Table.Th><Table.Th>Updated</Table.Th><Table.Th /></Table.Tr></Table.Thead>
          <Table.Tbody>
            {(policies.data ?? []).map((p) => (
              <Table.Tr key={p.policy_id}>
                <Table.Td><Text size="sm">{p.scope_kind} <Text span c="dimmed" size="xs">{p.scope_id}</Text></Text></Table.Td>
                <Table.Td><Badge color={p.paused ? "red" : "green"}>{p.paused ? "PAUSED" : "active"}</Badge></Table.Td>
                <Table.Td>{p.reason ?? ""}</Table.Td>
                <Table.Td><Text size="xs">{p.actor}</Text></Table.Td>
                <Table.Td>{p.revision}</Table.Td>
                <Table.Td><Text size="xs">{new Date(p.updated_at).toLocaleString()}</Text></Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <Button size="compact-xs" variant="light" onClick={() => setInspect({ kind: p.scope_kind, id: p.scope_id })}>Status</Button>
                    {p.paused && <Button size="compact-xs" variant="light" color="green" onClick={() => resume.mutate({ scope_kind: p.scope_kind, scope_id: p.scope_id })}>Resume</Button>}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {inspect && (
        <>
          <Title order={4} mb="xs">Status — {inspect.kind} {inspect.id}</Title>
          <Stack gap="xs">
            {status.data?.accounts.map((a) => (
              <Paper key={a.account_id} withBorder p="xs">
                <Group justify="space-between">
                  <Text size="sm" fw={600}>{a.account_id}</Text>
                  <Group gap={4}>
                    {a.states.map((s) => <Badge key={s} color={STATE_LABEL[s]?.color ?? "gray"} variant="light">{STATE_LABEL[s]?.label ?? s}</Badge>)}
                  </Group>
                </Group>
                <Text size="xs" c="dimmed">
                  device {a.device_online ? "online" : "OFFLINE"} · requested rev {a.requested_revision} · applied rev {a.applied_revision ?? "—"} ·
                  resting orders {a.resting_orders} · cancellations pending {a.cancellations_pending} · held positions {a.held_positions}
                </Text>
              </Paper>
            ))}
            {status.data && status.data.accounts.length === 0 && <Text c="dimmed">No accounts are affected by this scope.</Text>}
          </Stack>
        </>
      )}
    </>
  );
}
